import React, { useState } from 'react'

import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import Divider from '@material-ui/core/Divider'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import Alert from '@material-ui/lab/Alert'
import AccountsBenefitModal from 'components/account-benefits-modal'
import StepDescription from '../step-description'
import CoachCard from './coach-card'
import { useStyles } from './styles'
import BookingTypeCard from 'components/booking-type-card'
import PaymentInformation from '../payment-information'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { validationSchema } from '../utils'
import { withStripeElements } from 'lib/stripe'
import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { gtmEvent } from 'lib/gtm'
import { useSnackbar } from 'notistack'
import { useMutation } from '@apollo/client'
import { PurchaseLessonPackQuery, REDEEM_LESSON_CREDIT_QUERY } from './query'
import CircularProgress from '@material-ui/core/CircularProgress'
import ArrowBackOutlinedIcon from '@material-ui/icons/ArrowBackOutlined'
import SelectLessonPack from '../components/SelectLessonPack'
import StepDescriptionMobile from '../step-description-mobile'

const LessonPackReviewLayout = ({
  previousUrl,
  lessonInfo,
  locationId,
  startDateTime,
  student,
  isComplete,
  setIsComplete,
  setLastDigits,
  setCardBrand,
  setSelectedLessonPackId,
  selectedLessonPackId,
  lessonPackQuantity,
  setLessonPackQuantity,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()

  const [
    purchaseLessonPack,
    { loading: purchaseLessonPackQueryLoading },
  ] = useMutation(PurchaseLessonPackQuery)

  const [
    redeemLessonCredit,
    { loading: redeemLessonCreditLoading },
  ] = useMutation(REDEEM_LESSON_CREDIT_QUERY, {
    onCompleted: data => {
      const { success, message } = data.redeemLessonCredit
      if (success) {
        enqueueSnackbar(message, {
          variant: 'success',
        })
      } else {
        enqueueSnackbar(message, {
          variant: 'error',
        })
      }
    },
  })

  const {
    coach,
    lessonPacks,
    category,
    title,
    facilities,
    durationInMinutes,
  } = lessonInfo
  const facility = facilities.find(({ id }) => id === locationId)

  const form = useForm({
    resolver: yupResolver(validationSchema(true)),
  })
  const stripe = useStripe()
  const elements = useElements()

  const onSubmit = () => async formValues => {
    const { error: stripeError, token: stripeToken } = await stripe.createToken(
      elements.getElement(CardNumberElement),
      {
        name: formValues.payment.name,
        address_country: 'US',
      },
    )

    if (stripeError) {
      enqueueSnackbar(stripeError.message, {
        variant: 'error',
      })
      return Promise.resolve()
    }
    return purchaseLessonPack({
      variables: {
        lessonPackId: selectedLessonPackId,
        stripeToken: stripeToken?.id,
      },
    })
      .then(({ data }) => {
        const { success, message } = data.purchaseLessonPack

        if (success) {
          enqueueSnackbar(message, {
            variant: 'success',
          })
          gtmEvent({
            event: 'formSubmit',
            formCategory: 'purchase-lesson-pack',
            formAction: 'clicked-complete-purchase-button',
          })
          redeemLessonCredit({
            variables: {
              studentId: student.id,
              lessonTypeId: lessonInfo.id,
              startDateTime,
              locationId: facility.id,
            },
          }).then(({ data }) => {
            const { success } = data.redeemLessonCredit
            if (success) {
              setIsComplete(!isComplete)
              setLastDigits(stripeToken.card.last4)
              setCardBrand(stripeToken.card.brand)
            }
          })
        } else {
          enqueueSnackbar(message, {
            variant: 'error',
          })
        }
      })
      .catch(error => {
        window.rg4js &&
          window.rg4js('send', {
            error: error,
            tags: ['lesson-pack', 'form-submit'],
          })
        enqueueSnackbar('An error has occurred. Please try again later.', {
          variant: 'error',
        })
      })
  }

  if (purchaseLessonPackQueryLoading || redeemLessonCreditLoading) {
    return (
      <Container className={classes.root}>
        <div className={classes.loadingStyles}>
          <CircularProgress color="primary" />
        </div>
      </Container>
    )
  }

  return (
    <Container className={classes.root}>
      <Grid container>
        <Grid item xs={12}>
          <Box flexGrow={1} display={{ xs: 'none', md: 'flex' }}>
            <Button href={previousUrl} startIcon={<ArrowBackOutlinedIcon />}>
              BACK
            </Button>
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2} className={classes.stepContainer}>
        <StepDescriptionMobile
          stepText="Step 3 of 3"
          previousUrl={previousUrl}
        />
        <StepDescription
          title="Review"
          description="Please review the information below."
        />
      </Grid>
      <Grid container spacing={2}>
        <Grid item md={6} xs={12} className={classes.textWrapped}>
          <CoachCard coach={coach} />
          <BookingTypeCard
            category={category}
            facility={facility}
            title={title}
            durationInMinutes={durationInMinutes}
            startDateTime={startDateTime}
            isComplete={isComplete}
            lessonPacks={lessonPacks}
          />
          <Divider style={{ margin: '8px 0px 40px' }} />
          <>
            <SelectLessonPack
              lessonPacks={lessonPacks}
              selectedLessonPackId={selectedLessonPackId}
              setSelectedLessonPackId={setSelectedLessonPackId}
              setLessonPackQuantity={setLessonPackQuantity}
            />
            <FormProvider {...form}>
              <form>
                <Box mb={4}>
                  <PaymentInformation
                    lessonType={lessonInfo}
                    paymentRequired={true}
                  />
                </Box>
                {lessonPackQuantity !== undefined && (
                  <Alert severity="info" className={classes.warningBox}>
                    Once the purchase is complete, we will apply one of the
                    lessons from your pack. You will have {lessonPackQuantity}{' '}
                    lessons left.
                  </Alert>
                )}
                <Alert severity="info" className={classes.warningBox}>
                  Your credit card information is not being stored in our
                  system.
                </Alert>
                <Grid item md={5}>
                  <Box mb={4}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={form.handleSubmit(onSubmit())}
                      fullWidth
                      disabled={!selectedLessonPackId}
                      data-testid="complete-booking-button"
                    >
                      Complete Purchase
                    </Button>
                  </Box>
                </Grid>
              </form>
            </FormProvider>
          </>
        </Grid>
      </Grid>
      <AccountsBenefitModal isOpen={isOpen} setIsOpen={setIsOpen} />
    </Container>
  )
}

export default withStripeElements(LessonPackReviewLayout)
